import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarClock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/format";

const per = (iv?: string) => (iv === "year" ? "al año" : "al mes");

// Qué pasa con el débito automático de Armony: cuánto se suma, desde cuándo y cuándo hay acceso.
// q es la respuesta de /payments/debit/quote.
export function DebitQuoteSummary({ q }: { q: any }) {
  return (
    <div className="p-4 rounded-xl border border-navy/20 bg-navy/5 text-sm text-slate-800 space-y-2" data-testid="plan-checkout-summary">
      <p className="flex items-start gap-2"><CalendarClock className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
        {q.mode === "alta" ? (q.immediate_access
          ? <span>Tendrás <strong>{q.plan_name}</strong> desde hoy.</span>
          : <span>Tendrás <strong>{q.plan_name}</strong> cuando Armony confirme el primer cobro.</span>)
        : q.mode === "inmediato" ? <span>Tendrás <strong>{q.plan_name}</strong> desde hoy.</span>
        : q.mode === "mantener" ? <span>Mantienes tu plan actual; el cambio que habías programado queda sin efecto.</span>
        : <span>El cambio aplica desde el <strong>{q.effective_label}</strong>. Hasta entonces sigues con tu plan actual.</span>}
      </p>
      {q.mode === "alta" && <p>Se sumará <strong>{fmtMoney(q.recurring_amount)} {per(q.plan_interval)}</strong> a tu débito automático de Armony. El primer cobro aparecerá en tu próximo débito.</p>}
      {q.mode === "inmediato" && <>
        {q.one_time_amount > 0 && <p>Por los días que faltan de tu periodo actual se sumará <strong>una sola vez {fmtMoney(q.one_time_amount)}</strong> a tu próximo débito.</p>}
        <p>Desde el <strong>{q.next_period_label}</strong> se debitará <strong>{fmtMoney(q.recurring_amount)} {per(q.plan_interval)}</strong>{q.interval_change ? ", con el cambio de modalidad que elegiste" : ""}.</p>
      </>}
      {q.mode === "siguiente_periodo" && <p>Desde esa fecha se debitará <strong>{fmtMoney(q.recurring_amount)} {per(q.plan_interval)}</strong>. No hay cobros adicionales ni saldos a favor.</p>}
      {q.mode !== "mantener" && <p className="text-xs text-slate-600">Si Armony no puede realizar el cobro te avisaremos. Si no se confirma en {q.grace_days} días, el plan termina y sigues en Armony Contigo gratis.</p>}
    </div>
  );
}

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  lookupKey: string | null;
  planLabel: string;
  onDone: () => void;
};

// Cambio de plan de un cliente que ya paga por débito automático de Armony.
// Contratar un plan nuevo se hace en la vista /portal/planes/contratar (elige la forma de pago).
export function PlanCheckoutDialog({ open, onOpenChange, lookupKey, planLabel, onDone }: Props) {
  const [quote, setQuote] = useState<any>(null);
  const [failed, setFailed] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !lookupKey) return;
    setQuote(null); setFailed("");
    api.get("/payments/debit/quote", { params: { lookup_key: lookupKey } }).then((r) => setQuote(r.data)).catch((e) => setFailed(apiError(e)));
  }, [open, lookupKey]);

  const confirm = async () => {
    if (!lookupKey) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/change-plan", { lookup_key: lookupKey });
      toast.success(data.message || "Tu plan fue actualizado");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const q = quote;
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900 sm:max-w-lg" data-testid="plan-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Cambiar a {planLabel}</DialogTitle>
          <DialogDescription className="text-slate-600">Revisa cómo y cuándo se cobra antes de confirmar.</DialogDescription>
        </DialogHeader>
        {failed ? (
          <p className="text-sm text-red-700" role="alert">{failed}</p>
        ) : !q ? (
          <div className="flex justify-center py-8" role="status"><Loader2 className="w-6 h-6 animate-spin text-navy" aria-hidden="true" /><span className="sr-only">Cargando…</span></div>
        ) : (
          <DebitQuoteSummary q={q} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-slate-200 text-slate-700 rounded-full">Volver</Button>
          <Button data-testid="plan-checkout-confirm" onClick={confirm} disabled={busy || !q || !!failed || q?.mode === "igual"} className="gold-gradient-bg text-white font-semibold rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : q?.mode === "mantener" ? "Mantener mi plan" : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
